/**
 * Shared 401-refresh coordination for all axios clients.
 *
 * Why this exists:
 * Before this module, each axios client (main, agent, data-service, workflow,
 * knowledge) had its own 401 handler. The main client did silent refresh →
 * retry → fall back to a re-auth modal; the agent client jumped straight to
 * the modal; the data-service client did nothing at all. Result: an access
 * token whose `jti` had fallen out of the agent-service's Redis whitelist
 * (e.g. after Redis restart or after the entry's TTL) would silently fail
 * exactly one tab at a time, with no consistent recovery path. Symptoms
 * looked like "the admin screen shows orgs/users but not communities" —
 * the underlying cause was the asymmetric 401 handling.
 *
 * What this module provides:
 *  - One shared "refresh in progress" flag so concurrent 401s across clients
 *    coordinate on a single /auth/refresh round-trip rather than stampeding.
 *  - `handle401WithSilentRefresh` — the canonical 401 handler. Every axios
 *    client's response interceptor should delegate to this on 401.
 *  - `fallToReAuthModal` — the fallback when silent refresh fails.
 *
 * Behavior on 401:
 *  1. If no token was attached → reject normally (no point refreshing).
 *  2. If another client is already refreshing → subscribe and replay the
 *     original request with the new token (10s safety timeout).
 *  3. Otherwise: call refreshToken(). On success, replay original. On
 *     failure (revoked refresh cookie, network error, etc.), open the
 *     re-auth modal and queue the request for retry after re-auth.
 *
 * Server-side error strings to be aware of (all map to 401, all handled
 * identically here): "Token has been revoked", "Token has been blacklisted",
 * "JWT token has expired", "Invalid JWT token". The behavior is intentionally
 * uniform — distinguishing them adds no value because the recovery is the
 * same.
 */

import {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import {
  isReAuthModalOpen,
  triggerReAuth,
  useReAuthStore,
} from "@/stores/auth/reauth-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import {
  getCurrentAuthTokens,
  getCurrentUserEmail,
} from "@/services/api/auth-session";
import {
  authDebug,
  authDebugWarn,
  describeAuthError,
} from "@/stores/auth/auth-diagnostics";

const REFRESH_WAIT_TIMEOUT_MS = 10_000;

// Module-level state: shared across every client that uses this module.
// Imports from this file get the same singletons, so concurrent 401s in
// different axios instances dedupe to one refresh call.
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function notifyRefreshComplete(token: string): void {
  const subs = refreshSubscribers;
  refreshSubscribers = [];
  for (const cb of subs) {
    try {
      cb(token);
    } catch (err) {
      // A subscriber callback throwing must not break the rest of the chain.
      authDebugWarn("authRefresh.subscriber.threw", {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }
}

function notifyRefreshFailed(): void {
  // Drop subscribers without invoking; each one's caller will fall through
  // to the timeout path in handle401WithSilentRefresh and then to modal.
  refreshSubscribers = [];
}

function addRefreshSubscriber(cb: (token: string) => void): void {
  refreshSubscribers.push(cb);
}

/**
 * Open the re-auth modal and queue this request for retry once the user
 * re-authenticates. Returns a promise that resolves with the retried
 * response or rejects if the user dismisses the modal.
 */
export function fallToReAuthModal(
  client: AxiosInstance,
  originalRequest: InternalAxiosRequestConfig,
  userEmail: string | undefined,
): Promise<AxiosResponse> {
  if (!isReAuthModalOpen()) {
    triggerReAuth("token_invalid", userEmail || undefined);
  }
  return new Promise<AxiosResponse>((resolve, reject) => {
    useReAuthStore.getState().addPendingRequest({
      config: originalRequest,
      resolve: (result) => {
        if (result === "retry") {
          const newTokens = getCurrentAuthTokens();
          if (newTokens?.accessToken) {
            originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          }
          resolve(client(originalRequest));
        } else {
          reject(new Error("Re-authentication required"));
        }
      },
      reject: (err) => reject(err instanceof Error ? err : new Error(String(err))),
    });
  });
}

interface Handle401Options {
  /**
   * Some clients (e.g. the main client) need to short-circuit BEFORE this
   * handler runs — for example to skip refresh on public auth endpoints.
   * If `skipRefresh` is true, this handler immediately falls to the modal.
   */
  skipRefresh?: boolean;
}

/**
 * Canonical 401 handler. Call this from any axios client's response
 * interceptor when the response status is 401 and the request has not
 * already been retried.
 *
 * The caller is responsible for:
 *  - Setting `originalRequest._retry = true` BEFORE calling this so we
 *    don't loop on a second 401 after refresh.
 *  - Filtering out 401s that should not trigger refresh (e.g. the refresh
 *    endpoint itself, public auth endpoints — pass `skipRefresh: true` or
 *    skip calling this handler entirely).
 */
export async function handle401WithSilentRefresh(
  error: AxiosError,
  client: AxiosInstance,
  options: Handle401Options = {},
): Promise<AxiosResponse> {
  const originalRequest = error.config;
  if (!originalRequest) {
    throw error;
  }

  const hadToken = getCurrentAuthTokens();
  const userEmail = getCurrentUserEmail() ?? undefined;
  const requestUrl = originalRequest.url || "";

  // No token → no point refreshing; let the caller decide what to do.
  if (!hadToken?.accessToken) {
    authDebug("authRefresh.401.no-token", { requestUrl });
    throw error;
  }

  if (options.skipRefresh) {
    return fallToReAuthModal(client, originalRequest, userEmail);
  }

  // Concurrent 401: another client already started a refresh. Subscribe and
  // replay the request when the refresh completes, or fall to modal if it
  // takes too long.
  if (isRefreshing) {
    return new Promise<AxiosResponse>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error("refresh-wait-timeout"));
      }, REFRESH_WAIT_TIMEOUT_MS);

      addRefreshSubscriber((newToken) => {
        clearTimeout(timeoutId);
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        resolve(client(originalRequest));
      });
    }).catch(() => fallToReAuthModal(client, originalRequest, userEmail));
  }

  // First 401 in this refresh window — attempt silent refresh.
  isRefreshing = true;
  try {
    authDebug("authRefresh.401.refresh.start", { requestUrl });
    await useAuthStore.getState().refreshToken();
    const newTokens = getCurrentAuthTokens();
    const newAccessToken = newTokens?.accessToken || "";
    isRefreshing = false;
    notifyRefreshComplete(newAccessToken);

    if (!newAccessToken) {
      authDebugWarn("authRefresh.401.refresh.no-token-after", { requestUrl });
      return fallToReAuthModal(client, originalRequest, userEmail);
    }

    authDebug("authRefresh.401.refresh.success", {
      requestUrl,
      retrying: true,
    });
    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
    return client(originalRequest);
  } catch (refreshError) {
    isRefreshing = false;
    notifyRefreshFailed();
    authDebugWarn("authRefresh.401.refresh.failed", {
      requestUrl,
      ...describeAuthError(refreshError),
    });
    return fallToReAuthModal(client, originalRequest, userEmail);
  }
}

/**
 * Test helper: clear the refresh-in-progress state so tests don't leak
 * across cases. Not for production use.
 */
export function __resetAuthRefreshStateForTests(): void {
  isRefreshing = false;
  refreshSubscribers = [];
}
